import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Typography,
} from '@mui/material';
import { Add, Extension, Folder, History, Layers } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import ApiExecutionAddons from './ApiExecutionAddons';

const sidebarAwareDialogSx = (maxPx: number) => ({
  '& .MuiDialog-container': {
    paddingLeft: '16px',
    paddingRight: '16px',
    boxSizing: 'border-box',
  },
  '& .MuiDialog-paper': {
    width: `min(${maxPx}px, calc(100vw - clamp(172px, 13.4375vw, 280px) - 48px))`,
    maxWidth: `min(${maxPx}px, calc(100vw - clamp(172px, 13.4375vw, 280px) - 48px))`,
    margin: 'auto',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
});

export const TENANT_ADDON_REFRESH_EVENT = 'tenant-addon-refresh';

type TenantAddonCard = {
  addonCode: string;
  displayName: string;
  description?: string;
  currentOfferCapacity: number;
  currentOfferPrice: number;
  billingModel: string;
  canPurchase: boolean;
  purchasedCapacity: number;
  used?: number | null;
  limit?: number | null;
  retentionDays?: number | null;
  purchasedRetentionDays?: number | null;
};

const FALLBACK_ADDONS: TenantAddonCard[] = [
  {
    addonCode: 'PROJECTS',
    displayName: '+5 Projects',
    description: 'Expand workspace capacity without upgrading your plan tier.',
    currentOfferCapacity: 5,
    currentOfferPrice: 19,
    billingModel: 'ONE_TIME',
    canPurchase: true,
    purchasedCapacity: 0,
    used: 0,
    limit: 3,
  },
  {
    addonCode: 'ENVIRONMENTS',
    displayName: '+10 Environments per Project',
    description: 'Adds 10 environment slots to every project in your workspace.',
    currentOfferCapacity: 10,
    currentOfferPrice: 9,
    billingModel: 'ONE_TIME',
    canPurchase: true,
    purchasedCapacity: 0,
    used: 0,
    limit: 10,
  },
  {
    addonCode: 'EXTENDED_TEST_PLAN_HISTORY',
    displayName: 'Extended Test Plan History',
    description: 'Keep test run results for 365 days instead of 90 days.',
    currentOfferCapacity: 365,
    currentOfferPrice: 15,
    billingModel: 'ONE_TIME',
    canPurchase: true,
    purchasedCapacity: 0,
    retentionDays: 90,
  },
];

const addonIcon = (code: string) => {
  switch (code) {
    case 'PROJECTS':
      return <Folder color="primary" />;
    case 'ENVIRONMENTS':
      return <Layers color="primary" />;
    case 'EXTENDED_TEST_PLAN_HISTORY':
      return <History color="primary" />;
    default:
      return <Extension color="primary" />;
  }
};

const packLabel = (card: TenantAddonCard) => {
  if (card.addonCode === 'EXTENDED_TEST_PLAN_HISTORY') {
    return `${card.currentOfferCapacity} days retention`;
  }
  if (card.addonCode === 'PROJECTS') {
    return `+${card.currentOfferCapacity} projects`;
  }
  if (card.addonCode === 'ENVIRONMENTS') {
    return `+${card.currentOfferCapacity} environments per project`;
  }
  return `+${card.currentOfferCapacity}`;
};

const buttonLabel = (code: string) => {
  switch (code) {
    case 'PROJECTS':
      return 'Add Project Pack';
    case 'ENVIRONMENTS':
      return 'Add Environment Pack';
    case 'EXTENDED_TEST_PLAN_HISTORY':
      return 'Add History Pack';
    default:
      return 'Purchase';
  }
};

const SubscriptionAddons: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [addons, setAddons] = useState<TenantAddonCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [purchasingCode, setPurchasingCode] = useState<string | null>(null);
  const [dialogCard, setDialogCard] = useState<TenantAddonCard | null>(null);

  const loadData = useCallback(async (opts?: { soft?: boolean }) => {
    try {
      if (!opts?.soft) {
        setLoading(true);
      }
      setError(null);
      const [addonResponse, usageResponse] = await Promise.all([
        apiService.getTenantAddons().catch(() => ({ addons: [] as TenantAddonCard[] })),
        apiService.getSubscriptionUsage().catch(() => null),
      ]);

      let cards = addonResponse.addons ?? [];
      if ('error' in addonResponse && addonResponse.error) {
        setError(addonResponse.error);
      }

      if (cards.length === 0) {
        cards = FALLBACK_ADDONS.map((f) => ({ ...f }));
      }

      if (usageResponse && !usageResponse.error) {
        cards = cards.map((card) => {
          if (card.addonCode === 'PROJECTS' && usageResponse.projects) {
            const usageLimit = usageResponse.projects.limit;
            const cardLimit = card.limit;
            return {
              ...card,
              used: usageResponse.projects.used,
              // Prefer the higher effective cap so a stale usage overwrite cannot hide new packs.
              limit:
                cardLimit != null && usageLimit != null
                  ? Math.max(cardLimit, usageLimit)
                  : cardLimit ?? usageLimit,
            };
          }
          if (card.addonCode === 'ENVIRONMENTS' && usageResponse.environments) {
            const usageLimit = usageResponse.environments.limit;
            const cardLimit = card.limit;
            return {
              ...card,
              used: usageResponse.environments.used,
              limit:
                cardLimit != null && usageLimit != null
                  ? Math.max(cardLimit, usageLimit)
                  : cardLimit ?? usageLimit,
            };
          }
          if (card.addonCode === 'EXTENDED_TEST_PLAN_HISTORY' && usageResponse.history) {
            return {
              ...card,
              retentionDays:
                card.retentionDays != null
                  ? Math.max(card.retentionDays, usageResponse.history.retentionDays ?? 0)
                  : usageResponse.history.retentionDays,
              purchasedRetentionDays:
                card.purchasedCapacity > 0 ? card.purchasedCapacity : card.purchasedRetentionDays,
            };
          }
          return card;
        });
      }

      setAddons(cards);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load subscription add-ons');
      setAddons(FALLBACK_ADDONS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let cancelled = false;
    const timeouts: number[] = [];
    const handler = () => {
      const run = () => {
        if (!cancelled) {
          void loadData({ soft: true });
        }
      };
      run();
      timeouts.push(window.setTimeout(run, 400));
      timeouts.push(window.setTimeout(run, 1500));
      setSuccess('Add-on purchased successfully. Your limits have been updated.');
    };
    window.addEventListener(TENANT_ADDON_REFRESH_EVENT, handler);
    // Same return URL flag as ApiExecutionAddons — refresh even if the window event was missed.
    if (searchParams.get('tenantAddonRefresh') === 'true') {
      handler();
    }
    return () => {
      cancelled = true;
      window.removeEventListener(TENANT_ADDON_REFRESH_EVENT, handler);
      timeouts.forEach((id) => window.clearTimeout(id));
    };
  }, [loadData, searchParams]);

  const handleCheckout = async () => {
    if (!dialogCard) return;
    try {
      setPurchasingCode(dialogCard.addonCode);
      setError(null);
      const response = await apiService.createTenantAddonCheckoutSession(dialogCard.addonCode, 1);
      if (!response.success || !response.checkoutUrl) {
        setError(response.message || 'Failed to start checkout');
      } else {
        window.location.href = response.checkoutUrl;
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start checkout');
    } finally {
      setPurchasingCode(null);
      setDialogCard(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Extension color="primary" />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Subscription Add-ons
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={6} xl={3}>
          <ApiExecutionAddons />
        </Grid>

        {addons.map((card) => {
        const isHistory = card.addonCode === 'EXTENDED_TEST_PLAN_HISTORY';
        const used = card.used ?? 0;
        const limit = card.limit;
        const usagePct =
          limit != null && limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
        const purchasedPacks = isHistory
          ? card.purchasedCapacity > 0
            ? 1
            : 0
          : Math.floor((card.purchasedCapacity || 0) / Math.max(1, card.currentOfferCapacity));
        const price = Number(card.currentOfferPrice);

        return (
          <Grid item xs={12} sm={6} lg={6} xl={3} key={card.addonCode}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                {addonIcon(card.addonCode)}
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {isHistory ? 'Extended Test Plan History' : card.displayName}
                </Typography>
              </Box>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                {isHistory ? (
                  <>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Current retention
                      </Typography>
                      <Typography variant="h5">{card.retentionDays ?? 90} days</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        With add-on
                      </Typography>
                      <Typography variant="h5">{card.currentOfferCapacity} days</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Active add-on packs
                      </Typography>
                      <Typography variant="h5">{purchasedPacks}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {packLabel(card)} · ${price.toFixed(0)} one-time
                      </Typography>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Used
                      </Typography>
                      <Typography variant="h5">{used.toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Limit
                      </Typography>
                      <Typography variant="h5">
                        {limit == null || limit < 0 ? 'Unlimited' : limit.toLocaleString()}
                      </Typography>
                      {card.purchasedCapacity > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {card.addonCode === 'ENVIRONMENTS'
                            ? `Includes +${card.purchasedCapacity.toLocaleString()} per project from add-ons`
                            : `Includes +${card.purchasedCapacity.toLocaleString()} from add-ons`}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Active add-on packs
                      </Typography>
                      <Typography variant="h5">{purchasedPacks}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {packLabel(card)} each · ${price.toFixed(0)} one-time
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>

              {!isHistory && limit != null && limit > 0 && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={usagePct}
                    color={usagePct >= 100 ? 'error' : usagePct >= 80 ? 'warning' : 'primary'}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
              )}

              {card.canPurchase ? (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setDialogCard(card)}
                  disabled={purchasingCode === card.addonCode}
                >
                  {buttonLabel(card.addonCode)}
                </Button>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {card.description
                    || 'Available on Standard and Professional plans.'}
                </Typography>
              )}
            </CardContent>
          </Card>
          </Grid>
        );
      })}
      </Grid>

      <Dialog
        open={!!dialogCard}
        onClose={() => setDialogCard(null)}
        maxWidth={false}
        sx={sidebarAwareDialogSx(444)}
      >
        <DialogTitle>{dialogCard ? buttonLabel(dialogCard.addonCode) : 'Purchase'}</DialogTitle>
        <DialogContent>
          {dialogCard && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {dialogCard.description}
              </Typography>
              <Typography variant="body2">
                {packLabel(dialogCard)} · ${Number(dialogCard.currentOfferPrice).toFixed(0)} one-time
                (permanent — no recurring charge).
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogCard(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCheckout}
            disabled={!!purchasingCode}
          >
            {purchasingCode ? 'Redirecting…' : 'Continue to Checkout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionAddons;
